/**
 * Client for the BR1-native marketplace.
 *
 * Distinct from `features/marketplace`, which is the Magic Eden browser: that
 * one shows what BR1 NFTs are worth elsewhere and hands the user off to pay in
 * SOL from their own wallet. This one trades inside BR1, settled against the
 * deposited USD balance, with BR1 holding the NFT in between.
 */

import { getCookie } from '~/utils/cookie'

const BASE_URL = '/api/marketplace'

const csrftoken = (): string => getCookie('csrftoken') ?? ''

export type TradeNft = {
    mint: string
    name: string
    operative_type: string
    operative_id: number | null
    rank: number | null
    image_url: string | null
}

export type TradeListing = {
    id: number
    price: string
    state: number
    state_label: string
    created_at: string
    nft: TradeNft
    seller: { username: string }
    /** Only present on the viewer's own listings. */
    is_mine?: boolean
    review_reason?: string | null
}

/** Mirrors `marketplace.models.ListingState`. Named so a screen says what it
 *  means rather than comparing against a bare 3. */
export const ListingState = {
    AWAITING_ESCROW: 1,
    PENDING_REVIEW: 2,
    ACTIVE: 3,
    CANCELLING: 5,
    STUCK: 8,
} as const

/** Mirrors `marketplace.models.OrderState`. */
export const OrderState = {
    PAID: 1,
    DELIVERING: 2,
    COMPLETE: 3,
    FAILED: 4,
    NEEDS_ATTENTION: 5,
} as const

export type TradeOrder = {
    id: number
    listing_id: number
    state: number
    state_label: string
    price: string
    fee: string
    proceeds: string
    created_at: string
    delivered_at: string | null
    /** Only present once the order is complete. */
    delivery_signature: string | null
    nft: {
        mint: string
        name: string
        operative_type: string
        image_url: string | null
    }
}

/** The rules the server will actually enforce. Fetched rather than hardcoded:
 *  the fee and the review threshold are editable from Team Tools, so a client
 *  that assumes 20% will eventually display a number that is not true. */
export type TradeInfo = {
    fee_percent: string
    review_threshold: string
    min_price: string
    max_price: string
    escrow_available: boolean
}

export type ListingPage = {
    results: TradeListing[]
    page: number
    total_pages: number
    total_items: number
    has_next: boolean
}

export type CreatedListing = {
    listing: TradeListing
    /** Base64, already partially signed by the escrow wallet as fee payer --
     *  the seller adds the owner signature only, and needs no SOL. */
    transaction: string
    cluster_url: string
}

export type Challenge = {
    nonce: string
    /** Sign these exact bytes. Returned in full rather than rebuilt client-side
     *  so the string signed and the string verified cannot drift apart. */
    message: string
    expires_at: string
    wallet: string
}

/** The server answers errors as `{ message, code, errors }`. `code` is the part
 *  worth branching on; `message` is for showing. */
export class TradeError extends Error {
    code: string

    constructor(message: string, code: string) {
        super(message)
        this.code = code
    }
}

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
    const response = await fetch(`${BASE_URL}${path}`, {
        ...init,
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrftoken(),
            ...(init?.headers ?? {}),
        },
    })

    if (!response.ok) {
        // A non-JSON body here means something failed before the view ran --
        // a 403 from the staff gate, or a 500. Saying so beats "undefined".
        const body = await response.json().catch(() => null)
        throw new TradeError(
            body?.message ?? 'Something went wrong. Please try again.',
            body?.code ?? `http_${response.status}`
        )
    }
    return response.json()
}

export type Sellable = {
    mint: string
    name: string
    operative_type: string
    operative_id: number | null
    rank: number | null
    image_url: string | null
    wallet: string | null
    listable: boolean
    reason: string | null
}

export type SellablePage = {
    results: Sellable[]
    counts: Record<string, number>
    page: number
    total: number
    has_next: boolean
}

export const fetchSellable = (params: {
    category?: string
    search?: string
    sort?: string
    page?: number
}) => {
    const query = new URLSearchParams()
    if (params.category) query.set('category', params.category)
    if (params.search) query.set('search', params.search)
    if (params.sort) query.set('sort', params.sort)
    if (params.page) query.set('page', String(params.page))
    return request<SellablePage>(`/sellable/?${query.toString()}`)
}

/** Unstake so an NFT can be listed, without leaving the Sell tab.
 *
 *  Goes through `request` rather than a bare fetch so the CSRF token is sent --
 *  Django refuses an unauthenticated POST otherwise, and the failure looks like
 *  a permissions bug rather than a missing header. */
export const unstakeForListing = (mint: string) =>
    request<{ mint: string; staked: boolean }>('/unstake/', {
        method: 'POST',
        body: JSON.stringify({ mint }),
    })

export const fetchInfo = (): Promise<TradeInfo> => request('/info/')

export type ListingSort = 'newest' | 'price' | '-price'

export const fetchListings = (params: {
    type?: string
    search?: string
    page?: number
    sort?: ListingSort
}) => {
    const query = new URLSearchParams()
    if (params.type) query.set('type', params.type)
    if (params.sort) query.set('sort', params.sort)
    if (params.search) query.set('search', params.search)
    if (params.page) query.set('page', String(params.page))
    return request<ListingPage>(`/?${query.toString()}`)
}

export const fetchMyListings = () => request<{ results: TradeListing[] }>('/mine/')

/** Purchases by default; `sales` returns what the viewer sold. */
export const fetchMyOrders = (role: 'purchases' | 'sales' = 'purchases') =>
    request<{ results: TradeOrder[] }>(role === 'sales' ? '/orders/?role=sales' : '/orders/')

export const createListing = (body: { mint: string; wallet: string; price: string }) =>
    request<CreatedListing>('/listings/', { method: 'POST', body: JSON.stringify(body) })

export const markSubmitted = (listingId: number, signature: string) =>
    request<{ listing: TradeListing }>(`/listings/${listingId}/submitted/`, {
        method: 'POST',
        body: JSON.stringify({ signature }),
    })

export const requestChallenge = (listingId: number, action: 'cancel' | 'edit') =>
    request<Challenge>(`/listings/${listingId}/challenge/`, {
        method: 'POST',
        body: JSON.stringify({ action }),
    })

export const cancelListing = (listingId: number, nonce: string, signature: string) =>
    request<{ listing: TradeListing }>(`/listings/${listingId}/cancel/`, {
        method: 'POST',
        body: JSON.stringify({ nonce, signature }),
    })

export const editPrice = (listingId: number, price: string, nonce: string, signature: string) =>
    request<{ listing: TradeListing }>(`/listings/${listingId}/price/`, {
        method: 'POST',
        body: JSON.stringify({ price, nonce, signature }),
    })

/**
 * Buy a listing.
 *
 * `expectedPrice` is what the card displayed. The server rejects the purchase
 * if the listing has moved since, so a seller cannot edit the price out from
 * under a click.
 *
 * `idempotencyKey` must be generated once per attempt and reused across
 * retries -- that is what makes a double-click or a flaky connection one
 * debit rather than two.
 */
export const buyListing = (body: {
    listingId: number
    wallet: string
    expectedPrice: string
    idempotencyKey: string
}) =>
    request<{ order: TradeOrder }>(`/listings/${body.listingId}/buy/`, {
        method: 'POST',
        body: JSON.stringify({
            wallet: body.wallet,
            expected_price: body.expectedPrice,
            idempotency_key: body.idempotencyKey,
        }),
    })
